import { useEffect, useState, type FunctionComponent } from "react";
import { Form } from "../../../../components/ui/form";
import { FormInput } from "../../../../components/form-components/form-input.components";
import { FormMultiSelect } from "../../../../components/form-components/form-multiselect.components";
import { FormMultiSelectGenre } from "../../../../components/form-components/form-multiselect-genre.components";
import { Button } from "../../../../components/ui/button";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  type IzManageFiltersFormAdmin,
  zManageFiltersFormAdmin,
} from "../../../../z/admin/manage-filters-form.admin.z";
import { SocialGenderOptions } from "../../campaign-form.admin.partials";
import { useGetSocialGenreAdminHttp } from "../../../../hooks/http/admin/social-genre/get-social-genre.admin.http";
import type { IGenreMinimal } from "../../../../types/models/genre.models.types";
import { useToast } from "../../../../hooks/use-toast";
import { findErrorMessage, handleHttpError } from "../../../../utils/utils";
import { useProposalSocialApplyFilterAdminHttp } from "../../../../hooks/http/admin/proposal-creators/proposal-social-apply-filter.admin.http";
import { useParams } from "react-router-dom";
import Spinner from "../../../../components/spinner.components";
import useProposalSocialStore from "../../../../store/proposal-creators.store";

export const ManageFiltersAdminPartial: FunctionComponent = () => {
  const [genreOptions, setGenreOptions] = useState<IGenreMinimal[]>([]);
  const { execute: getGenreOptionsHttp, fetching: fetchingGenres } =
    useGetSocialGenreAdminHttp();
  const { toast } = useToast();
  const { setData: setProposalSocials } = useProposalSocialStore();

  const { execute: applyFilterHttp, fetching: fetchingApplyFilter } =
    useProposalSocialApplyFilterAdminHttp();

  const { proposalId } = useParams();

  async function onGenreFetch() {
    const response = await getGenreOptionsHttp({
      body: null,
    });
    if (!response.success) {
      return handleHttpError(response, toast);
    }
    setGenreOptions(response.data.genres);
  }

  const form = useForm<IzManageFiltersFormAdmin>({
    resolver: zodResolver(zManageFiltersFormAdmin),
    defaultValues: {
      filters: {
        followers: undefined,
        gender: [],
        genre: [],
      },
    },
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (form.formState.errors) {
      const key = Object.keys(form.formState.errors)[0];
      if (key) {
        const error =
          form.formState.errors[key as keyof typeof form.formState.errors];
        const message = findErrorMessage(error) || "An error occurred";

        toast({
          content: message,
          toastType: "error",
        });
      }
    }
  }, [form.formState.errors]);

  async function applyFilters(data: IzManageFiltersFormAdmin) {
    if (!proposalId) {
      return;
    }
    const response = await applyFilterHttp({
      body: {
        filter: {
          followers:
            data.filters?.followers?.max || data.filters?.followers?.min
              ? {
                  min: data.filters?.followers
                    ? data.filters.followers.min
                    : undefined,
                  max: data.filters?.followers
                    ? data.filters.followers.max
                    : undefined,
                }
              : undefined,
          gender:
            data.filters.gender && data.filters.gender.length > 0
              ? data.filters.gender.map((g: { value: string } | string) =>
                  typeof g === "string" ? g : g.value
                )
              : undefined,
          genre:
            data.filters.genre && data.filters.genre.length > 0
              ? data.filters.genre.map((genre) => genre.id)
              : undefined,
        },
      },
      pathParams: { proposalId },
    });
    if (!response.success) {
      return handleHttpError(response, toast);
    }
    setProposalSocials(response.data.campaignProposalSocials);
    document.getElementById("closeSheet")?.click();
  }

  const handleSubmit = async (data: IzManageFiltersFormAdmin) => {
    console.log(data);
    await applyFilters(data);
  };

  return (
    <div className="relative flex flex-col w-full h-full">
      <div className="top-0 sticky flex items-center gap-5 px-4 pb-6 border-b border-border w-full">
        <p className="font-semibold text-xl">Manage filters</p>
      </div>
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(handleSubmit)}
          className="space-y-6 px-4 pt-10 pb-10"
        >
          {/* Followers Range */}
          <div className="space-y-4">
            <div className="gap-4 grid grid-cols-2">
              <FormInput
                form={form}
                name="filters.followers.min"
                label="Minimum followers"
                placeholder="Enter minimum followers"
                type="number"
              />
              <FormInput
                form={form}
                name="filters.followers.max"
                label="Maximum followers"
                placeholder="Enter maximum followers"
                type="number"
              />
            </div>
          </div>

          {/* Gender Selection */}
          <div className="space-y-4">
            <FormMultiSelect
              form={form}
              name="filters.gender"
              label="Gender preference"
              placeholder="Select gender preferences"
              options={Object.values(SocialGenderOptions).map((option) => ({
                value: option.value,
                label: option.label,
              }))}
            />
          </div>

          {/* Genre Selection */}
          <div className="space-y-4">
            <FormMultiSelectGenre
              form={form}
              name="filters.genre"
              label="Content genres"
              placeholder="Select content genres"
              options={
                genreOptions.map((genre) => ({
                  id: genre.id,
                  title: genre.title,
                  icon: genre.emoji,
                })) || []
              }
              fetcher={onGenreFetch}
              fetching={fetchingGenres}
            />
          </div>
        </form>
      </Form>
      {/* Submit Button */}
      <div className="bottom-0 left-0 absolute flex justify-end px-4 py-4 w-full">
        <Button
          onClick={() => {
            form.handleSubmit(handleSubmit)();
          }}
          disabled={!form.formState.isDirty}
          className="bg-primarybuttonbg py-7 w-full font-semibold text-black"
        >
          {fetchingApplyFilter ? <Spinner width={20} /> : "Update"}
        </Button>
      </div>
    </div>
  );
};
